<template>
  <div class="relative">
    <!-- Language Selector Button -->
    <button
      @click="isOpen = !isOpen"
      type="button"
      :class="[
        'inline-flex items-center justify-center rounded-lg transition-colors duration-200',
        compact
          ? 'p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          : 'px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800'
      ]"
      :title="compact ? $t('settings.language') : ''"
    >
      <!-- Flag Icon -->
      <span class="text-lg mr-2" v-if="!compact">{{ currentLocale.flag }}</span>
      <span class="text-lg" v-else>{{ currentLocale.flag }}</span>
      
      <!-- Language Name (only in full mode) -->
      <span v-if="!compact" class="hidden sm:block ml-1">{{ currentLocale.name }}</span>
      
      <!-- Dropdown Arrow (only in full mode) -->
      <svg
        v-if="!compact"
        class="ml-2 h-4 w-4 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        :class="[
          'absolute z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none',
          position === 'left' ? 'left-0' : 'right-0',
          compact ? 'w-48' : 'w-56'
        ]"
        role="menu"
      >
        <div class="py-1" role="none">
          <!-- Search Box (for larger lists) -->
          <div v-if="showSearch && availableLocales.length > 6" class="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('common.actions.search') + '...'"
              class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
          </div>

          <!-- Language Options -->
          <button
            v-for="locale in filteredLocales"
            :key="locale.code"
            @click="changeLanguage(locale.code)"
            :class="[
              'group flex items-center w-full px-3 py-2 text-sm transition-colors duration-150',
              locale.code === currentLocaleCode
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
            ]"
            role="menuitem"
          >
            <!-- Flag -->
            <span class="text-lg mr-3 flex-shrink-0">{{ locale.flag }}</span>
            
            <!-- Language Name -->
            <span class="flex-1 text-left">{{ locale.name }}</span>
            
            <!-- Active Indicator -->
            <svg
              v-if="locale.code === currentLocaleCode"
              class="ml-2 h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <!-- No Results (when searching) -->
          <div
            v-if="showSearch && searchQuery && filteredLocales.length === 0"
            class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center"
          >
            {{ $t('common.messages.no_results') }}
          </div>
        </div>
      </div>
    </transition>

    <!-- Overlay to close dropdown when clicking outside -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-40"
      @click="isOpen = false"
    ></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { setLocale, getCurrentLocale, getAvailableLocales, i18nLocaleToUrl } from '@/plugins/i18n'
import { useToast } from 'vue-toastification'
import logger from '@/services/logger'
import api from '@/plugins/axios'

const props = defineProps({
  compact: {
    type: Boolean,
    default: false
  },
  position: {
    type: String,
    default: 'right', // 'left' or 'right'
    validator: (value) => ['left', 'right'].includes(value)
  },
  showSearch: {
    type: Boolean,
    default: true
  }
})

const { t } = useI18n()
const toast = useToast()
const router = useRouter()
const route = useRoute()

// State
const isOpen = ref(false)
const searchQuery = ref('')
const currentLocaleCode = ref(getCurrentLocale())
const availableLocales = ref(getAvailableLocales())

// Computed
const currentLocale = computed(() => {
  return availableLocales.value.find(locale => locale.code === currentLocaleCode.value) || availableLocales.value[0]
})

const filteredLocales = computed(() => {
  if (!props.showSearch || !searchQuery.value) {
    return availableLocales.value
  }
  
  const query = searchQuery.value.toLowerCase()
  return availableLocales.value.filter(locale =>
    locale.name.toLowerCase().includes(query) ||
    locale.code.toLowerCase().includes(query)
  )
})

// Methods
const changeLanguage = async (localeCode) => {
  if (localeCode === currentLocaleCode.value) {
    isOpen.value = false
    return
  }

  try {
    const previousLocale = currentLocaleCode.value
    const success = setLocale(localeCode)

    if (success) {
      currentLocaleCode.value = localeCode
      isOpen.value = false
      searchQuery.value = ''

      // Navigate to the same route with new locale prefix
      const newUrlLocale = i18nLocaleToUrl(localeCode)
      const currentPath = route.fullPath

      // Replace the old locale prefix with the new one
      const newPath = currentPath.replace(/^\/(en|pt-br|es)/, `/${newUrlLocale}`)

      // Use router.push to navigate
      await router.push(newPath)

      // Show success message (after navigation to use new locale)
      const newLocale = availableLocales.value.find(l => l.code === localeCode)
      toast.success(t('settings.changes_saved'))

      // Log the language change
      logger.logUserAction('Language Changed', {
        from: previousLocale,
        to: localeCode,
        language: newLocale?.name,
        newPath
      })

      // Optionally sync with backend
      await syncLanguageWithBackend(localeCode)

    } else {
      toast.error(t('common.messages.error'))
    }
  } catch (error) {
    logger.error('Failed to change language', { error: error.message }, 'i18n')
    toast.error(t('common.messages.error'))
  }
}

const syncLanguageWithBackend = async (localeCode) => {
  // Only sync with backend if user is authenticated
  const authToken = localStorage.getItem('auth_token')
  if (!authToken) {
    return // Skip sync for unauthenticated users
  }

  try {
    // Update user preferences on the backend
    await api.patch('/user/preferences', {
      language: localeCode
    })
  } catch (error) {
    // Log error but don't show to user as language change still works locally
    logger.error('Failed to sync language preference with backend', {
      error: error.message,
      locale: localeCode
    }, 'i18n')
  }
}

const handleEscape = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* Custom transitions for smoother animations */
.transition-transform {
  transition-property: transform;
}

/* Ensure dropdown appears above other elements */
.z-50 {
  z-index: 50;
}

.z-40 {
  z-index: 40;
}

/* Focus styles for accessibility */
button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

:root.dark button:focus-visible {
  outline-color: #60a5fa;
}

/* Hover effects */
.group:hover .flex-shrink-0 {
  transform: scale(1.1);
  transition: transform 0.15s ease-in-out;
}
</style>